import { isSuccess, isUnauthorized, type IHTTPResponse } from '@yimoka/shared';
import { action, define, observable } from '@yimoka/store';

import { authAccountEntity } from '@/entities/auth-account';
import type {
  IAuthAccountOAuth2Provider,
  IAuthAccountStaff,
  IAuthAccountStaffAuthReply,
  IAuthAccountUserAuthReply,
} from '@/entities/auth-account.d';
import {
  clearAuthSession,
  clearTenantAuthSession,
  getStaffToken,
  getTenantID,
  getUserToken,
  setStaff,
  setStaffToken,
  setTenant,
  setTenantID,
  setTenantStaffs,
  setUser,
  setUserToken,
} from '@/session/auth-session';
import {
  extractTenantIDFromRedirect,
  isPlatformUserRedirect,
  normalizeAuthFlowRedirect,
  normalizeInternalRedirect,
  replaceTenantIDInRedirect,
  type TenantSelectorMode,
} from '@/utils/auth-redirect';
import { decodeOAuthStateContext, encodeOAuthStateContext } from '@/utils/oauth-state';

type Navigate = (to: string, options?: { replace?: boolean }) => unknown;

// accountOAuth2CallbackPath 与 tenant-web 注册的 OAuth 回调路由保持一致。
const accountOAuth2CallbackPath = '/oauth2/callback';

/** normalizeAccountOAuth2Providers 按当前站点选择回调地址，并同步改写授权 URL。 */
export function normalizeAccountOAuth2Providers(
  providers: IAuthAccountOAuth2Provider[] | undefined,
  currentOrigin = typeof window === 'undefined' ? '' : window.location.origin,
) {
  if (!currentOrigin) return [];
  return (providers ?? []).flatMap((provider) => {
    const redirectURL = provider.redirectURLs?.find((value) => {
      try {
        const callbackURL = new URL(value);
        return callbackURL.origin === currentOrigin && callbackURL.pathname === accountOAuth2CallbackPath;
      } catch {
        return false;
      }
    });
    if (!redirectURL || !provider.authorizeURL) return [];

    try {
      const authorizeURL = new URL(provider.authorizeURL, currentOrigin);
      authorizeURL.searchParams.set('redirect_uri', redirectURL);
      return [{ ...provider, authorizeURL: authorizeURL.toString() }];
    } catch {
      return [];
    }
  });
}

class AuthAccountBiz {
  activeLoginMode: 'phone' | 'password' = 'phone';
  redirect = '';
  selectedStaffID = '';
  disabledTenantModalOpen = false;
  tenantLoginMask = false;
  tenantSelectorStatus: 'error' | 'idle' | 'loading' | 'ready' = 'idle';
  oauth2Status: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  private autoLoginStarted = false;

  /** 初始化登录、租户选择与 OAuth 回调所需的响应式状态。 */
  constructor() {
    define(this, {
      activeLoginMode: observable,
      redirect: observable,
      selectedStaffID: observable,
      disabledTenantModalOpen: observable,
      tenantLoginMask: observable,
      tenantSelectorStatus: observable,
      oauth2Status: observable,
      setActiveLoginMode: action,
      setRedirect: action,
      setSelectedStaffID: action,
      setDisabledTenantModalOpen: action,
      setTenantLoginMask: action,
      setTenantSelectorStatus: action,
      setOauth2Status: action,
      mountLoginPage: action,
      mountTenantSelector: action,
      mountOAuth2Callback: action,
      sendPhoneCaptcha: action,
      handleUserLoginSuccess: action,
      handleTenantLogin: action,
      switchLoginMethod: action,
    });
  }

  /** authConfigStore 暴露登录页唯一的 BaseUser 认证配置 Store。 */
  get authConfigStore() {
    return authAccountEntity.authConfigStore;
  }

  get passwordLoginStore() {
    return authAccountEntity.passwordLoginStore;
  }

  /** userInfoStore 提供认证入口主动校验平台用户 token 的原子请求状态。 */
  get userInfoStore() {
    return authAccountEntity.userInfoStore;
  }

  get phoneLoginStore() {
    return authAccountEntity.phoneLoginStore;
  }

  get phoneCaptchaStore() {
    return authAccountEntity.phoneCaptchaStore;
  }

  get tenantStaffsStore() {
    return authAccountEntity.tenantStaffsStore;
  }

  get tenantLoginStore() {
    return authAccountEntity.tenantLoginStore;
  }

  get oauth2LoginStore() {
    return authAccountEntity.oauth2LoginStore;
  }

  /** platformLogoutStore 暴露平台用户退出操作状态，供租户选择页提供局部 pending 反馈。 */
  get platformLogoutStore() {
    return authAccountEntity.platformLogoutStore;
  }

  /** tenantLogoutStore 暴露当前单位退出状态，供切换登录方式串行收口双层会话。 */
  get tenantLogoutStore() {
    return authAccountEntity.tenantLogoutStore;
  }

  /** passwordEnabled 只读取 BaseUser 认证配置，避免部署配置和内容对象形成第二事实源。 */
  get passwordEnabled() {
    return this.authConfigStore.response?.data?.login?.enablePassword === true;
  }

  /** phoneEnabled 只在 BaseUser 明确启用手机登录时展示入口。 */
  get phoneEnabled() {
    return this.authConfigStore.response?.data?.login?.enablePhone === true;
  }

  /** oauth2Providers 返回 BaseUser 已启用的第三方登录公共投影。 */
  get oauth2Providers() {
    return normalizeAccountOAuth2Providers(this.authConfigStore.response?.data?.oauth2);
  }

  /** authConfigReady 仅在认证配置成功返回后允许渲染确定性的空状态。 */
  get authConfigReady() {
    return isSuccess(this.authConfigStore.response);
  }

  get staffs() {
    const data = this.tenantStaffsStore.response?.data;
    return Array.isArray(data) ? data : [];
  }

  get selectedStaff() {
    return this.staffs.find(item => item.id === this.selectedStaffID);
  }

  /** enabledStaffs 只返回员工和租户均启用的可登录身份。 */
  get enabledStaffs() {
    return this.staffs.filter(item => item.switch !== false && item.tenant?.switch !== false);
  }

  setActiveLoginMode = (mode: 'phone' | 'password') => {
    this.activeLoginMode = mode;
  };

  /** setRedirect 只保存安全站内目标，所有认证完成后的跳转都从同一边界读取。 */
  setRedirect = (redirect?: string | null) => {
    this.redirect = normalizeAuthFlowRedirect(redirect) ?? '';
  };

  setSelectedStaffID = (staffID?: string) => {
    this.selectedStaffID = staffID ?? '';
  };

  setDisabledTenantModalOpen = (open: boolean) => {
    this.disabledTenantModalOpen = open;
  };

  setTenantLoginMask = (visible: boolean) => {
    this.tenantLoginMask = visible;
  };

  /** setTenantSelectorStatus 区分用户态校验与租户列表请求，错误时允许用户显式重试。 */
  setTenantSelectorStatus = (status: AuthAccountBiz['tenantSelectorStatus']) => {
    this.tenantSelectorStatus = status;
  };

  setOauth2Status = (status: AuthAccountBiz['oauth2Status']) => {
    this.oauth2Status = status;
  };

  /** buildOAuth2URL 把 Web 业务目标放入兼容 state，第三方回调后才能恢复邀请或深链。 */
  buildOAuth2URL = (provider: IAuthAccountOAuth2Provider, redirect = this.redirect) => {
    const raw = provider.authorizeURL ?? '';
    if (!raw) return '';
    const safeRedirect = normalizeInternalRedirect(redirect);
    const state = encodeOAuthStateContext(provider.clientID, safeRedirect, provider.provider);
    try {
      const url = new URL(raw, window.location.origin);
      url.searchParams.set('state', state);
      return url.toString();
    } catch {
      const joiner = raw.includes('?') ? '&' : '?';
      return `${raw}${joiner}state=${encodeURIComponent(state)}`;
    }
  };

  /** mountLoginPage 恢复已有登录态，并按邀请、租户深链和普通入口选择后续页面。 */
  mountLoginPage = async (navigate: Navigate, redirect?: string | null) => {
    this.setRedirect(redirect);
    if (!this.authConfigStore.response?.data && !this.authConfigStore.loading) {
      void this.authConfigStore.fetch();
    }

    const userToken = getUserToken();
    if (!userToken) return;

    // 租户邀请链路只需要用户态，已有用户 token 时直接回到邀请页。
    if (this.redirect && isPlatformUserRedirect(this.redirect)) {
      navigate(this.redirect);
      return;
    }

    const tenantID = getTenantID();
    const staffToken = getStaffToken();
    if (this.redirect) {
      navigate(staffToken && tenantID ? this.redirect : `/tenant-selector?redirect=${encodeURIComponent(this.redirect)}`);
      return;
    }

    navigate(staffToken && tenantID ? `/t/${tenantID}/workspace` : '/tenant-selector');
  };

  handleUserLoginSuccess = async (
    res: Partial<IHTTPResponse<IAuthAccountUserAuthReply>>,
    navigate: Navigate,
  ) => {
    await this.completeUserLogin(res, navigate);
  };

  /** mountTenantSelector 校验平台用户并按页面来源决定是否自动进入单租户。 */
  mountTenantSelector = async (
    navigate: Navigate,
    redirect?: string | null,
    mode: TenantSelectorMode = 'auto',
  ) => {
    this.setRedirect(redirect);
    this.setTenantSelectorStatus('loading');
    const loginRedirect = this.redirect || '/tenant-selector';
    if (!getUserToken()) {
      navigate(`/login?redirect=${encodeURIComponent(loginRedirect)}`);
      return;
    }

    const userRes = await this.userInfoStore.fetch();
    if (isUnauthorized(userRes)) {
      clearAuthSession();
      navigate(`/login?redirect=${encodeURIComponent(loginRedirect)}`);
      return;
    }
    if (!isSuccess(userRes)) {
      this.setTenantSelectorStatus('error');
      return;
    }
    if (userRes.data?.token) setUserToken(userRes.data.token);
    if (userRes.data?.user) setUser(userRes.data.user);
    this.setTenantSelectorStatus('ready');

    const res = await this.fetchTenantStaffs();
    const staffs = Array.isArray(res.data) ? res.data : [];
    // 只有首次登录流程才自动进入唯一租户；用户主动切换时必须保留选择页。
    if (mode === 'auto' && staffs.length === 1 && !this.autoLoginStarted) {
      const [staff] = staffs;
      if (staff?.tenant?.switch === false || staff?.switch === false) return;
      this.autoLoginStarted = true;
      await this.handleTenantLogin(staff, navigate);
    }
  };

  handleTenantLogin = async (staff: IAuthAccountStaff, navigate: Navigate, targetRedirect = this.redirect): Promise<boolean> => {
    if (staff.switch === false || staff.tenant?.switch === false) {
      this.setSelectedStaffID(staff.id);
      this.setDisabledTenantModalOpen(true);
      return false;
    }

    const tenantID = String(staff.tenant?.id || staff.tenantID);
    this.setSelectedStaffID(staff.id);
    this.setTenantLoginMask(true);
    setTenantID(tenantID);
    setTenant(staff.tenant);
    this.tenantLoginStore.setValues({ staffID: staff.id, tenantID });
    const res = await this.tenantLoginStore.fetch();
    this.setTenantLoginMask(false);
    if (!isSuccess(res)) return false;
    const reply = res.data;
    if (!reply) return false;
    this.completeTenantLogin(reply, tenantID, navigate, targetRedirect);
    return true;
  };

  /** switchLoginMethod 按员工态、平台态顺序退出双层会话，失败时保留仍有效的凭证供用户重试。 */
  switchLoginMethod = async (): Promise<boolean> => {
    if (this.tenantLogoutStore.loading || this.platformLogoutStore.loading) return false;

    if (getStaffToken()) {
      const tenantRes = await this.tenantLogoutStore.fetch();
      if (!isSuccess(tenantRes)) return false;

      // 员工退出成功后凭证已失效，必须立即清除；平台退出失败时仍可用用户态单独重试。
      clearTenantAuthSession();
    }

    const res = await this.platformLogoutStore.fetch();
    if (!isSuccess(res)) return false;

    // 服务端退出成功后再清理本地 token，避免失败时出现前后端会话状态不一致。
    clearAuthSession();
    return true;
  };

  sendPhoneCaptcha = async () => {
    const { phone, phonePrefix } = this.phoneLoginStore.values;
    this.phoneCaptchaStore.setValues({ phone, phonePrefix });
    return this.phoneCaptchaStore.fetch();
  };

  /** mountOAuth2Callback 恢复 Web 业务目标、交换一次性 code，并复用普通用户登录完成分支。 */
  mountOAuth2Callback = async (
    navigate: Navigate,
    code?: string | null,
    state?: string | null,
    redirect?: string | null,
    oauthError?: string | null,
  ) => {
    const stateContext = decodeOAuthStateContext(state ?? '');
    // 新版 state 承载发起登录时的真实业务目标；callback query 只兼容旧链接，不能反向覆盖邀请或深链。
    this.setRedirect(
      normalizeInternalRedirect(stateContext.redirect)
      ?? normalizeInternalRedirect(redirect),
    );
    if (oauthError) {
      this.setOauth2Status('error');
      return;
    }
    this.setOauth2Status('loading');
    const configRes = this.authConfigStore.response?.data
      ? this.authConfigStore.response
      : await this.authConfigStore.fetch();

    if (!isSuccess(configRes)) {
      this.setOauth2Status('error');
      return;
    }

    // Web OAuth2 入口复用登录配置中的 provider 列表，保证 state 与 clientID 可追溯。
    this.oauth2LoginStore.setValues({
      callbackURL: typeof window === 'undefined' ? '' : window.location.href,
      code: code ?? '',
      state: state ?? '',
      oauth2List: configRes.data?.oauth2 ?? [],
    });
    const loginRes = await this.oauth2LoginStore.fetch();
    if (!isSuccess(loginRes)) {
      this.setOauth2Status('error');
      return;
    }
    this.setOauth2Status('success');
    await this.completeUserLogin(loginRes, navigate);
  };

  /** dispose 释放账号入口共享 Store。 */
  dispose = () => {
    this.autoLoginStarted = false;
    authAccountEntity.disposeOperation('authConfig');
    authAccountEntity.disposeOperation('passwordLogin');
    authAccountEntity.disposeOperation('phoneLogin');
    authAccountEntity.disposeOperation('phoneCaptcha');
    authAccountEntity.disposeOperation('oauth2Login');
    authAccountEntity.disposeOperation('tenantStaffs');
    authAccountEntity.disposeOperation('tenantLogin');
    authAccountEntity.disposeOperation('myMenus');
    authAccountEntity.disposeOperation('tenantLogout');
    authAccountEntity.disposeOperation('platformLogout');
  };

  private fetchTenantStaffs = async () => {
    const res = await this.tenantStaffsStore.fetch();
    if (isSuccess(res)) {
      setTenantStaffs(Array.isArray(res.data) ? res.data : []);
    }
    return res;
  };

  private completeUserLogin = async (
    res: Partial<IHTTPResponse<IAuthAccountUserAuthReply>>,
    navigate: Navigate,
  ) => {
    if (!isSuccess(res)) return;
    if (res.data?.token) setUserToken(res.data.token);
    if (res.data?.user) setUser(res.data.user);

    if (this.redirect && isPlatformUserRedirect(this.redirect)) {
      navigate(this.redirect);
      return;
    }

    if (this.redirect) {
      const staffsRes = await this.fetchTenantStaffs();
      if (!isSuccess(staffsRes) || !Array.isArray(staffsRes.data)) {
        navigate(`/tenant-selector?redirect=${encodeURIComponent(this.redirect)}`);
        return;
      }
      const redirectTenantID = extractTenantIDFromRedirect(this.redirect);
      // redirect 已指定 /t/{tenantID} 时，优先尝试匹配该租户的员工身份直达目标页。
      const staff = staffsRes.data.find(item => (
        redirectTenantID
        && (String(item.tenantID) === redirectTenantID || item.tenant?.id === redirectTenantID)
      ));
      if (staff) {
        const loggedIn = await this.handleTenantLogin(staff, navigate, this.redirect);
        if (loggedIn) return;
      }
      navigate(`/tenant-selector?redirect=${encodeURIComponent(this.redirect)}`);
      return;
    }

    const staffsRes = await this.fetchTenantStaffs();
    const staffs = Array.isArray(staffsRes.data) ? staffsRes.data : [];
    if (staffs.length === 1) {
      const [staff] = staffs;
      if (staff && staff.switch !== false && staff.tenant?.switch !== false) {
        const loggedIn = await this.handleTenantLogin(staff, navigate);
        if (loggedIn) return;
      }
    }

    navigate('/tenant-selector');
  };

  /** completeTenantLogin 落盘租户员工态，并把安全业务目标映射到实际选择的租户。 */
  private completeTenantLogin(reply: IAuthAccountStaffAuthReply, tenantID: string, navigate: Navigate, targetRedirect?: string) {
    if (reply.token) setStaffToken(reply.token, tenantID);
    if (reply.staff) setStaff(reply.staff, tenantID);
    const replyRedirect = normalizeInternalRedirect(reply.redirect);
    if (replyRedirect) {
      navigate(replyRedirect);
      return;
    }
    const safeTargetRedirect = normalizeInternalRedirect(targetRedirect);
    if (safeTargetRedirect) {
      const redirectTenantID = extractTenantIDFromRedirect(safeTargetRedirect);
      // 用户选择的租户和 redirect 中租户不一致时，保留业务路径但替换租户前缀。
      navigate(!redirectTenantID || redirectTenantID === tenantID
        ? safeTargetRedirect
        : replaceTenantIDInRedirect(safeTargetRedirect, tenantID));
      return;
    }
    navigate(`/t/${tenantID}/workspace`);
  }
}

export const authAccountBiz = new AuthAccountBiz();
